import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, MFA_REQUIRED_CODE } from '../api/client';

import { accueilPourRole, useAuth } from './AuthContext';
import { DefiAntiRobot } from './DefiAntiRobot';
import { MotDePasseOublie } from './MotDePasseOublie';

interface LoginLocationState {
  readonly from?: string;
}

/**
 * Écran de connexion de la console.
 *
 * Flux MFA : l'API répond `401 { code: MFA_REQUIRED }` quand l'OTP manque
 * (obligatoire pour tuteur / admin). On révèle alors le champ code et on
 * resoumet e-mail + mot de passe + `otp`.
 */
export function LoginScreen() {
  const { status, user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LoginLocationState | null)?.from ?? null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpRequired, setOtpRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // AB#747 — « Mot de passe oublié ? » remplace la carte, sans changer d'adresse.
  const [oubli, setOubli] = useState(false);

  if (status === 'authenticated' && user) {
    return <Navigate to={from ?? accueilPourRole(user.role)} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const connecte = await login(email, password, otpRequired ? otp : undefined);
      navigate(from ?? accueilPourRole(connecte.role), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === MFA_REQUIRED_CODE) {
        setError(otpRequired ? 'Code de vérification incorrect. Réessayez.' : null);
        setOtpRequired(true);
      } else if (err instanceof ApiError) {
        setError(err.message || 'Connexion impossible.');
      } else {
        setError('Connexion impossible. Vérifiez votre connexion réseau.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (oubli) {
    return (
      <div className="auth-screen">
        <MotDePasseOublie emailInitial={email} onRetour={() => setOubli(false)} />
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <div className="auth-card__brand">
          Silex
          <span className="brand-dot" aria-hidden="true" />
        </div>
        <p className="form-note">Console tuteur et administration.</p>

        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="field">
          <label htmlFor="login-email">Adresse courriel</label>
          <input
            id="login-email"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="login-password">Mot de passe</label>
          <input
            id="login-password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {otpRequired ? (
          <div className="field">
            <label htmlFor="login-otp">Code de vérification</label>
            <input
              id="login-otp"
              type="text"
              name="otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            {!error ? (
              <span className="form-note">
                Saisissez le code à 6 chiffres de votre application d'authentification.
              </span>
            ) : null}
          </div>
        ) : null}

        {/* AB#884 — le défi anti-robot, seulement avec une clé de site. */}
        <DefiAntiRobot />

        <button type="submit" className="btn" disabled={submitting}>
          {submitting ? 'Connexion…' : 'Se connecter'}
        </button>

        <p style={{ marginTop: 16 }}>
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => setOubli(true)}>
            Mot de passe oublié ?
          </button>
        </p>
      </form>
    </div>
  );
}
